import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import SessionExpiredNotice from './components/SessionExpiredNotice';
import KIQLogoAnimado from './components/design-system/KIQLogoAnimado';
import { puedeUsarKIQ } from './utils/permisosKiq';

// Cada página se carga en su propio chunk (React.lazy) en vez de ir todas en
// el bundle principal — FE-013. El fallback mientras carga un chunk es la
// misma pantalla de marca que ya se usaba para el estado de auth "cargando"
// (PantallaCarga, más abajo), así nunca se ve una pantalla en blanco. Si un
// chunk falla al cargar (ej. deploy nuevo con hashes viejos en caché), el
// ErrorBoundary de Layout.js (o el de index.js para /login y /registro, que no
// usan Layout) lo capturan igual que cualquier otro error de render — no hizo
// falta ErrorBoundary nuevo para esto.
const Login = lazy(() => import('./pages/Login'));
const Registro = lazy(() => import('./pages/Registro'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Ingredientes = lazy(() => import('./pages/Ingredientes'));
const Recetas = lazy(() => import('./pages/Recetas'));
const Inventario = lazy(() => import('./pages/Inventario'));
const Facturas = lazy(() => import('./pages/Facturas'));
const Ventas = lazy(() => import('./pages/Ventas'));
const PnL = lazy(() => import('./pages/PnL'));
const Academia = lazy(() => import('./pages/Academia'));
const AcademiaAdmin = lazy(() => import('./pages/AcademiaAdmin'));
const MenuEngineering = lazy(() => import('./pages/MenuEngineering'));
const Superadmin = lazy(() => import('./pages/Superadmin'));
const Perfil = lazy(() => import('./pages/Perfil'));
const Equipo = lazy(() => import('./pages/Equipo'));
const Objetivos = lazy(() => import('./pages/Objetivos'));
const DesignSystem = lazy(() => import('./pages/DesignSystem'));
const Kiq = lazy(() => import('./pages/Kiq'));
const Proveedores = lazy(() => import('./pages/Proveedores'));
const Marketing = lazy(() => import('./pages/Marketing'));
// Beta — recuperación de contraseña y páginas legales (públicas).
const RecuperarPassword = lazy(() => import('./pages/RecuperarPassword'));
const RestablecerPassword = lazy(() => import('./pages/RestablecerPassword'));
const Privacidad = lazy(() => import('./pages/legal/Privacidad'));
const Terminos = lazy(() => import('./pages/legal/Terminos'));

function RutaProtegida({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return <PantallaCarga />;
  return usuario ? <Layout>{children}</Layout> : <Navigate to="/login" />;
}

function RutaPublica({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return <PantallaCarga />;
  return usuario ? <Navigate to="/dashboard" /> : children;
}

function RutaAdmin({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return <PantallaCarga />;
  return usuario?.rol === 'admin' ? <Layout>{children}</Layout> : <Navigate to="/academia" />;
}

// Beta — /kiq solo para admin/gerente (utils/permisosKiq.js; el backend también lo
// exige desde SH-2): cajero y cocinero vuelven al panel en vez de ver un error.
function RutaKiq({ children }) {
  const { usuario, cargando } = useAuth();
  if (cargando) return <PantallaCarga />;
  if (!usuario) return <Navigate to="/login" />;
  return puedeUsarKIQ(usuario.rol) ? <Layout>{children}</Layout> : <Navigate to="/dashboard" replace />;
}

function RutaSuperadmin({ children }) {
  const { usuario, cargando, esSuperadmin } = useAuth();
  if (cargando) return <PantallaCarga />;
  if (!usuario) return <Navigate to="/login" />;
  return esSuperadmin ? <Layout>{children}</Layout> : <Navigate to="/dashboard" replace />;
}

// Pantalla de carga (design_handoff_carga) — mismos 4 usos de siempre
// (verificación de sesión en las 4 Ruta* de abajo + fallback de Suspense
// para cada chunk lazy) — solo cambia su CONTENIDO, nunca las rutas/guards
// ni la estructura de Suspense. Fondo #07070E siempre (nunca var(--...):
// se muestra antes de que ThemeContext resuelva ningún tema), igual que el
// resto de la app oscura de esta pantalla — así nunca hay parpadeo blanco
// entre esta pantalla y el resto de la app (checklist del README).
function PantallaCarga() {
  return (
    <div role="status" aria-label="Cargando GastroKIQ" style={{
      minHeight: '100vh',
      background: 'radial-gradient(420px 320px at 50% 42%, rgba(123,58,237,.22), transparent 70%), #07070E',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '26px',
    }}>
      <KIQLogoAnimado variante="arma" size={132} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '28px', fontWeight: '800', letterSpacing: '-.025em', color: '#F5F6FA' }}>
          Gastro<span style={{ background: 'linear-gradient(90deg,#4C86FF,#FF2D8A)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>KIQ</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#8487A0' }}>
          Cargando
          <span style={{ display: 'flex', gap: '3px' }} aria-hidden="true">
            <span className="kiq-carga-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#FF8FC4', animation: 'kiqdots 1.2s infinite' }} />
            <span className="kiq-carga-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#FF8FC4', animation: 'kiqdots 1.2s infinite .2s' }} />
            <span className="kiq-carga-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#FF8FC4', animation: 'kiqdots 1.2s infinite .4s' }} />
          </span>
        </span>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <SessionExpiredNotice />
        <Suspense fallback={<PantallaCarga />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/login" element={<RutaPublica><Login /></RutaPublica>} />
            <Route path="/registro" element={<RutaPublica><Registro /></RutaPublica>} />
            <Route path="/recuperar-password" element={<RutaPublica><RecuperarPassword /></RutaPublica>} />
            {/* Sin guarda a propósito: quien abre el enlace del correo desde un navegador
                con otra sesión abierta no debe ser redirigido (perdería el token). */}
            <Route path="/restablecer-password" element={<RestablecerPassword />} />
            <Route path="/privacidad" element={<Privacidad />} />
            <Route path="/terminos" element={<Terminos />} />
            <Route path="/dashboard" element={<RutaProtegida><Dashboard /></RutaProtegida>} />
            <Route path="/ingredientes" element={<RutaProtegida><Ingredientes /></RutaProtegida>} />
            <Route path="/recetas" element={<RutaProtegida><Recetas /></RutaProtegida>} />
            <Route path="/inventario" element={<RutaProtegida><Inventario /></RutaProtegida>} />
            <Route path="/facturas" element={<RutaProtegida><Facturas /></RutaProtegida>} />
            <Route path="/ventas" element={<RutaProtegida><Ventas /></RutaProtegida>} />
            <Route path="/finanzas" element={<RutaProtegida><PnL /></RutaProtegida>} />
            <Route path="/objetivos" element={<RutaProtegida><Objetivos /></RutaProtegida>} />
            <Route path="/menu-engineering" element={<RutaProtegida><MenuEngineering /></RutaProtegida>} />
            <Route path="/superadmin" element={<RutaSuperadmin><Superadmin /></RutaSuperadmin>} />
            <Route path="/perfil" element={<RutaProtegida><Perfil /></RutaProtegida>} />
            <Route path="/equipo" element={<RutaProtegida><Equipo /></RutaProtegida>} />
            {/* Shell (design_handoff_shell) — 3 items nuevos del sidebar. Ninguno
                se restringe por rol (el propio README lo deja explícito: "si
                alguno debe hacerlo, se decide aparte"). */}
            <Route path="/kiq" element={<RutaKiq><Kiq /></RutaKiq>} />
            <Route path="/proveedores" element={<RutaProtegida><Proveedores /></RutaProtegida>} />
            <Route path="/marketing" element={<RutaProtegida><Marketing /></RutaProtegida>} />
            <Route path="/academia" element={<RutaProtegida><Academia /></RutaProtegida>} />
            <Route path="/academia/admin" element={<RutaAdmin><AcademiaAdmin /></RutaAdmin>} />
            {/* Design System v1 (Fase 1) — herramienta interna de desarrollo/QA, no
                una función de restaurante: admin-only, SIN entrada en el sidebar
                (Layout.js:navGroups) a propósito, para no exponerla por accidente
                a un flujo de navegación normal. Ver frontend/src/pages/DesignSystem.js. */}
            <Route path="/design-system" element={<RutaAdmin><DesignSystem /></RutaAdmin>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}
